import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import styles from './FaqFuture.module.css';

// FAQ 데이터를 직접 정의
const faqData = {
  faqs: [
    {
      question: "서비스 이용 방법",
      answer: "서비스 이용 방법에 대한 상세한 답변입니다. 웹사이트에 접속하여 회원가입 후 이용할 수 있습니다."
    },
    {
      question: "결제 방법",
      answer: "신용카드, 계좌이체, 간편결제 등 다양한 결제 수단을 지원합니다. 결제 페이지에서 원하는 결제 수단을 선택하여 진행할 수 있습니다."
    },
    {
      question: "환불 정책",
      answer: "환불은 서비스 이용 시작 후 7일 이내에 가능합니다. 고객센터를 통해 환불 신청을 할 수 있으며, 서비스 이용 시간에 따라 차등 환불됩니다."
    },
    {
      question: "계정 관리",
      answer: "계정 설정에서 프로필 수정, 비밀번호 변경, 알림 설정 등을 할 수 있습니다. 보안을 위해 정기적인 비밀번호 변경을 권장합니다."
    },
    {
      question: "기술 지원",
      answer: "기술적인 문제가 발생할 경우 고객센터(1234-5678)로 문의하시거나 support@example.com으로 이메일을 보내주세요."
    }
  ]
};

function FaqFuture() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Gemini API 초기화
  const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: '안녕하세요! 무엇을 도와드릴까요?'
    }]);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputValue
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const context = "Available FAQ information:\n" + 
        faqData.faqs.map(faq => 
          `Q: ${faq.question}\nA: ${faq.answer}`
        ).join('\n\n');

      const model = genAI.getGenerativeModel({ model: "gemini-pro" });

      const chat = model.startChat({
        history: [
          {
            role: "user",
            parts: [{
              text: `You are a helpful assistant that answers questions based on the provided FAQ information. 
              Only answer questions using the information provided in the FAQ. 
              If the question cannot be answered using the FAQ, politely say so.
              
              ${context}`
            }]
          },
          {
            role: "model",
            parts: [{
              text: "이해했습니다. FAQ 정보를 바탕으로 질문에 답변하도록 하겠습니다."
            }]
          }
        ],
      });

      const result = await chat.sendMessage(inputValue);
      const response = await result.response;

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.text()
      }]);
    } catch (error) {
      console.error('Error:', error);
      let errorMessage = '죄송합니다. 오류가 발생했습니다.';
      
      if (error.message.includes('429')) {
        errorMessage = '잠시 후 다시 시도해주세요. (API 할당량 초과)';
      }
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessage
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.chatContainer}>
        <div className={styles.messages}>
          {messages.map((message, index) => (
            <div
              key={index}
              className={`${styles.message} ${
                message.role === 'user' ? styles.userMessage : styles.botMessage
              }`}
            >
              {message.content}
            </div>
          ))}
          {isLoading && (
            <div className={`${styles.message} ${styles.botMessage}`}>
              <div className={styles.loadingDots}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className={styles.inputForm}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="질문을 입력하세요..."
            className={styles.input}
          />
          <button 
            type="submit" 
            className={styles.sendButton}
            disabled={isLoading || !inputValue.trim()}
          >
            전송
          </button>
        </form>
      </div>
    </div>
  );
}

export default FaqFuture; 